import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Home,
  PlusCircle,
  User,
  LogOut,
  BarChart,
  Menu as MenuIcon,
  X,
  MessageSquare,
  Lightbulb as LightBulb,
  Users,
  Brain,
  ChevronDown,
  ChevronUp,
  Zap,
} from 'lucide-react';
import { auth } from '../lib/firebase';
import { signOut } from 'firebase/auth';
import { useStore } from '../store';
import { createCheckoutSession } from '../services/usageService';
import { useRipple } from '../lib/useRipple';
import LanguageToggle from './LanguageToggle';
import UpgradeModal from './UpgradeModal';
import { useTranslation } from '../i18n/useTranslation';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const profile = useStore((state) => state.profile);
  const user = useStore((state) => state.user);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [mobileProfileDropdownOpen, setMobileProfileDropdownOpen] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const showUpgradeModal = useStore((state) => state.showUpgradeModal);
  const setShowUpgradeModal = useStore((state) => state.setShowUpgradeModal);
  const { createRipple } = useRipple();
  const { t } = useTranslation();
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/');
  };

  const handleUpgrade = async (event: React.MouseEvent<HTMLButtonElement>) => {
    createRipple(event);
    if (!user) return;
    setShowUpgradeModal(true);
  };

  const handleUpgradeConfirm = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { url } = await createCheckoutSession(user.uid);
      window.open(url, '_blank');
      setShowUpgradeModal(false);
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to start upgrade process');
    } finally {
      setLoading(false);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileProfileDropdownOpen(false);
  };

  const closeDesktopDropdown = () => {
    setProfileDropdownOpen(false);
  };

  // Close profile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false);
      }
    }

    if (profileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownOpen]);

  return (
    <>
      {/* Glassmorphism Navigation */}
      <nav className='glass-nav sticky top-0 z-50'>
        <div className='max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between h-16'>
            <div className='flex items-center'>
              <Link to='/' className='mr-8 flex-shrink-0 animate-float'>
                <img
                  src='https://cdn.shopify.com/s/files/1/0609/6397/9463/files/Logo_Goalr.png?v=1740666570'
                  alt='GoalR Logo'
                  className='h-8 w-auto transition-transform duration-300 hover:scale-110'
                />
              </Link>
              <div className='hidden md:flex items-center gap-1'>
                <Link to='/dashboard' className='nav-link'>
                  <Home className='h-5 w-5' />
                  <span className='ml-2'>{t('nav.games')}</span>
                </Link>
                <Link to='/new-game' className='nav-link'>
                  <PlusCircle className='h-5 w-5' />
                  <span className='ml-2'>{t('nav.newGame')}</span>
                </Link>
                <Link to='/analytics' className='nav-link'>
                  <BarChart className='h-5 w-5' />
                  <span className='ml-2'>{t('nav.analytics')}</span>
                </Link>
                <Link to='/ai-trainer' className='nav-link'>
                  <Brain className='h-5 w-5' />
                  <span className='ml-2'>{t('nav.aiTrainer')}</span>
                </Link>
                <Link to='/feedback' className='nav-link'>
                  <MessageSquare className='h-5 w-5' />
                  <span className='ml-2'>{t('nav.feedback')}</span>
                </Link>
              </div>
            </div>
            <div className='hidden md:flex items-center space-x-4'>
              <LanguageToggle />
              {/* Only show upgrade button if user hasn't paid */}
              {profile && profile.paymentStatus !== 'paid' && (
                <div className='relative'>
                  <button
                    onClick={handleUpgrade}
                    disabled={loading}
                    className='btn-upgrade-pro btn-ripple group whitespace-nowrap animate-pulse-glow'
                  >
                    <span className='absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-shimmer' />
                    <Zap className='h-5 w-5 mr-1' />
                    <span>{t('nav.upgradeToPro')}</span>
                  </button>
                  {error && (
                    <div
                      className='absolute top-full mt-2 left-0 glass-modal px-3 py-2 rounded-lg text-sm text-red-600 cursor-pointer hover:bg-red-50'
                      onClick={() => { setError(''); setShowUpgradeModal(true); }}
                    >
                      {error} — Click to retry
                    </div>
                  )}
                </div>
              )}

              {/* Profile Menu */}
              <div
                className='relative flex items-center'
                ref={profileDropdownRef}
              >
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className='nav-link'
                >
                  <User className='h-5 w-5' />
                  <span className='ml-2'>{t('nav.profile')}</span>
                  <ChevronDown className='h-4 w-4 ml-1' />
                </button>
                {profileDropdownOpen && (
                  <div className='absolute right-0 top-full mt-2 w-48 dropdown-glass z-50'>
                    <div className='py-2'>
                      <Link
                        to='/profile'
                        className='flex items-center px-4 py-3 text-sm text-slate-700 hover:text-primary-600 hover:bg-primary-50/50 transition-all duration-200'
                        onClick={closeDesktopDropdown}
                      >
                        <User className='h-4 w-4 mr-3' />
                        {t('nav.profile')}
                      </Link>
                      <Link
                        to='/coach-dashboard'
                        className='flex items-center px-4 py-3 text-sm text-slate-700 hover:text-primary-600 hover:bg-primary-50/50 transition-all duration-200'
                        onClick={closeDesktopDropdown}
                      >
                        <Users className='h-4 w-4 mr-3' />
                        {t('nav.coach')}
                      </Link>
                      <Link
                        to='/upvotes'
                        className='flex items-center px-4 py-3 text-sm text-slate-700 hover:text-primary-600 hover:bg-primary-50/50 transition-all duration-200'
                        onClick={closeDesktopDropdown}
                      >
                        <LightBulb className='h-4 w-4 mr-3' />
                        {t('nav.featureRequests')}
                      </Link>
                      <div className='border-t border-slate-200/50 my-2'></div>
                      <button
                        onClick={() => {
                          closeDesktopDropdown();
                          handleSignOut();
                        }}
                        className='flex items-center px-4 py-3 text-sm text-slate-700 hover:text-primary-600 hover:bg-primary-50/50 transition-all duration-200 w-full text-left'
                      >
                        <LogOut className='h-4 w-4 mr-3' />
                        {t('nav.signOut')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='md:hidden flex items-center'>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className='nav-link p-2'
              >
                {mobileMenuOpen ? (
                  <X className='h-6 w-6' />
                ) : (
                  <MenuIcon className='h-6 w-6' />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu with glassmorphism */}
        {mobileMenuOpen && (
          <div className='md:hidden glass-sidebar border-t border-white/20'>
            <div className='px-4 pt-4 pb-3 space-y-2'>
              {profile && profile.paymentStatus !== 'paid' && (
                <button
                  onClick={(e) => {
                    createRipple(e);
                    closeMobileMenu();
                    handleUpgrade(e);
                  }}
                  className='btn-upgrade-pro btn-ripple w-full mb-3 animate-pulse-glow'
                >
                  <Zap className='h-5 w-5' />
                  <span className='ml-2'>{t('nav.upgradeToPro')}</span>
                </button>
              )}
              <Link
                to='/dashboard'
                className='nav-link w-full'
                onClick={closeMobileMenu}
              >
                <Home className='h-5 w-5' />
                <span className='ml-3'>{t('nav.games')}</span>
              </Link>
              <Link
                to='/new-game'
                className='nav-link w-full'
                onClick={closeMobileMenu}
              >
                <PlusCircle className='h-5 w-5' />
                <span className='ml-3'>{t('nav.newGame')}</span>
              </Link>
              <Link
                to='/analytics'
                className='nav-link w-full'
                onClick={closeMobileMenu}
              >
                <BarChart className='h-5 w-5' />
                <span className='ml-3'>{t('nav.analytics')}</span>
              </Link>
              <Link
                to='/ai-trainer'
                className='nav-link w-full'
                onClick={closeMobileMenu}
              >
                <Brain className='h-5 w-5' />
                <span className='ml-3'>{t('nav.aiTrainer')}</span>
              </Link>
              <Link
                to='/feedback'
                className='nav-link w-full'
                onClick={closeMobileMenu}
              >
                <MessageSquare className='h-5 w-5' />
                <span className='ml-3'>{t('nav.feedback')}</span>
              </Link>

              {/* Language Toggle */}
              <div className='py-3'>
                <LanguageToggle />
              </div>

              {/* Mobile Profile Dropdown */}
              <div>
                <button
                  onClick={() =>
                    setMobileProfileDropdownOpen(!mobileProfileDropdownOpen)
                  }
                  className='nav-link w-full justify-between'
                >
                  <div className='flex items-center'>
                    <User className='h-5 w-5' />
                    <span className='ml-3'>{t('nav.profile')}</span>
                  </div>
                  {mobileProfileDropdownOpen ? (
                    <ChevronUp className='h-4 w-4' />
                  ) : (
                    <ChevronDown className='h-4 w-4' />
                  )}
                </button>
                {mobileProfileDropdownOpen && (
                  <div className='ml-6 mt-2 space-y-1'>
                    <Link
                      to='/profile'
                      className='flex items-center px-3 py-2 text-slate-700 hover:text-primary-600 hover:bg-primary-50/50 rounded-lg transition-all duration-200'
                      onClick={closeMobileMenu}
                    >
                      <User className='h-4 w-4' />
                      <span className='ml-3'>{t('nav.profile')}</span>
                    </Link>
                    <Link
                      to='/coach-dashboard'
                      className='flex items-center px-3 py-2 text-slate-700 hover:text-primary-600 hover:bg-primary-50/50 rounded-lg transition-all duration-200'
                      onClick={closeMobileMenu}
                    >
                      <Users className='h-4 w-4' />
                      <span className='ml-3'>{t('nav.coach')}</span>
                    </Link>
                    <Link
                      to='/upvotes'
                      className='flex items-center px-3 py-2 text-slate-700 hover:text-primary-600 hover:bg-primary-50/50 rounded-lg transition-all duration-200'
                      onClick={closeMobileMenu}
                    >
                      <LightBulb className='h-4 w-4' />
                      <span className='ml-3'>{t('nav.featureRequests')}</span>
                    </Link>
                    <div className='border-t border-slate-200/50 my-2 mx-3'></div>
                    <button
                      onClick={() => {
                        closeMobileMenu();
                        handleSignOut();
                      }}
                      className='flex items-center px-3 py-2 text-slate-700 hover:text-primary-600 hover:bg-primary-50/50 rounded-lg transition-all duration-200 w-full text-left'
                    >
                      <LogOut className='h-4 w-4' />
                      <span className='ml-3'>{t('nav.signOut')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {children}
      </main>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onConfirm={handleUpgradeConfirm}
        loading={loading}
      />
    </>
  );
}
